<template>
    <div class="document-container">
      <es-loader v-if="loading"/>
      <es-exception
        v-else-if="loadingError"
        margin="7.25rem"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
      />
      <div v-else>
        <div v-if="locationDetailsHidden">
          <transaction-profile-person-details
            :person="person"
            :userHasAccessToPersonLocation="userHasAccessToPersonLocation"
            style="margin-top: 0"
            />
          <transaction-profile-location-info
            @showDetails="showHideLocationDetails($event)"
            @emitToAddLocation="toggleAddLocation"
            :person="person"
            :userHasAccessToPersonLocation="userHasAccessToPersonLocation"
            :personLocations="personLocations"
            style="margin-top: 1rem"
            />
        </div>
        <div v-else>
          <transaction-profile-location-info-details
            @hideDetails="showHideLocationDetails"
            :personLocation="locationDetails"
            :userHasAccessToPersonLocation="userHasAccessToPersonLocation"
            style="margin-top: 1rem"
            />
        </div>
      </div>
    </div>
  </template>

<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faUser,
  faPhoneRotary,
  faListAlt,
  faDatabase,
  faDiploma,
  faAddressBook,
} from '@fortawesome/pro-solid-svg-icons';
import {
  mapActions,
  mapGetters,
} from 'vuex';
import EsLoader from '@/components/EsLoader.vue';
import EsException from '@/components/EsException.vue';
import TransactionProfilePersonDetails from '@/components/TransactionProfilePersonDetails.vue';
import TransactionProfileLocationInfo from '@/components/TransactionProfileLocationInfo.vue';
import TransactionProfileLocationInfoDetails from '@/components/TransactionProfileLocationInfoDetails.vue';
import FieldLabels from '@/mixins/FieldLabels';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faUser,
  faPhoneRotary,
  faListAlt,
  faDatabase,
  faDiploma,
  faAddressBook,
);

export default {
  mixins: [
    FieldLabels,
    PermissionManager,
  ],
  components: {
    EsLoader,
    EsException,
    TransactionProfilePersonDetails,
    TransactionProfileLocationInfo,
    TransactionProfileLocationInfoDetails,
  },
  props: {
    transactionId: {
      type: String,
      required: true,
    },
    transaction: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      loading: null,
      loadingError: null,
      person: {},
      personLocations: [],
      locationDetailsHidden: true,
      locationDetails: {},
    };
  },
  async created() {
    await this.onLoad();
    this.constructPersonLocations();
  },
  computed: {
    ...mapGetters('lookups', [
      'activeGenders',
      'activeStates',
      'activeRaces',
      'activeTitles',
      'activePersonTypes'
    ]),
    userlocationPermissions() {
      const choose = (src, ...keys) => keys.reduce(
        (result, key) => ({ ...result, [key]: src[key] }), {},
      );
      return choose(this.$store.state.user.locationPermissions[0], 'districts', 'schools');
    },
    userHasAccessToDistrict() {
      if (this.userlocationPermissions.districts.length) {
        return !!this.userlocationPermissions.districts
          .find(x => x.code === this.person.districtCode);
      } return true;
    },
    userHasAccessToPersonLocation() {
      if (this.userHasAccessToDistrict) {
        if (this.userlocationPermissions.schools.length) {
          return !!this.userlocationPermissions.schools
            .find(x => x.code === this.person.schoolCode);
        } return true;
      } return false;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadGenders',
      'loadStates',
      'loadRaces',
      'loadGrades',
      'loadTitles',
      'loadPersonTypes'
    ]),
    toggleAddLocation() {
      this.showAddLocation = !this.showAddLocation;
    },
    async getPerson() {
      const response = await axios.get(`/personid/submissions/${this.transactionId}`);
      this.person = response.data;
      return this.person;
    },
    getPersonTypeCategory() {
      const temp = this.activePersonTypes.find(a => a.code === this.person.personType);
      if (temp) {
        return temp.category;
      }
      return null;
    },
    getPersonTypeName() {
      const temp = this.activePersonTypes.find(a => a.code === this.person.personType);
      if (temp) {
        return temp.name;
      }
      return null;
    },
    constructPersonLocations() {
      const districtName = this.transaction.districtCode === this.person.districtCode
        ? this.transaction.districtName
        : '';
      const schoolName = this.transaction.schoolCode === this.person.schoolCode
        ? this.transaction.schoolName
        : '';
      this.personLocations = [{
        lastUpdateDate: this.transaction.lastUpdateDate,
        personType: this.person.personType,
        districtCode: this.person.districtCode,
        schoolCode: this.person.schoolCode,
        active: this.person.valid,
        gradeCode: this.person.gradeCode,
        schoolYear: this.person.schoolYear,
        altSourceCode: this.person.altSourceSisCode,
        localId: this.person.localStudentId,
        altPersonId: this.person.altStudentId,
        sourceCode: this.person.sourceCode,
        hireDate: this.person.hireDate,
        jobClassCode: this.person.jobClassCode,
        jobAssignment1: this.person.jobAssignment1,
        jobAssignment2: this.person.jobAssignment2,
        jobAssignment3: this.person.jobAssignment3,
        itinerantFlag: this.person.itinerantFlag,
        salary: this.person.salary,
        sourceRefreshDate: this.person.sourceRefreshDate,
        resDistrictCode: this.person.resDistrictCode,
        ocationComment: this.person.comments,
        districtName: districtName,
        schoolName: schoolName,
        personTypeCategory: this.getPersonTypeCategory(),
        personTypeName: this.getPersonTypeName(),
      }];
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        const res = await Promise.all([
          this.loadGenders(),
          this.loadStates(),
          this.loadRaces(),
          this.loadGrades(),
          this.loadTitles(),
          this.loadPersonTypes(),
          this.getPerson(),
        ]);
        await this.getPerson();
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    showHideLocationDetails(Object) {
      this.locationDetails = Object;
      this.locationDetailsHidden = !this.locationDetailsHidden;
    },
  },
  filters: {
  },
};
</script>

<style lang="sass" scoped>
</style>
